'use client'

/**
 * 43OS — znak marki.
 *
 * Lockup: „43" + żółty dymek „OS" (czarny tekst na żółtym — reguła kontrastu marki).
 * Renderowany w HTML/CSS, nie SVG — dzięki temu używa fontu aplikacji i jest ostry
 * w każdym rozmiarze. Wszystko skaluje się od `size` (px = wysokość wersalika).
 */

const YELLOW = '#FFD400'
const BLACK = '#111111'

type Variant = 'wordmark' | 'lockup' | 'mark'

interface Logo43OSProps {
  /** Wysokość wersalika w px. Domyślnie 28. */
  size?: number
  /** `wordmark` = 43OS · `lockup` = 43OS + tagline · `mark` = sam dymek (favicon, zwinięty sidebar) */
  variant?: Variant
  /** Na ciemnym tle „43" jest białe. */
  onDark?: boolean
  /** Tagline pod wordmarkiem (tylko `lockup`). */
  tagline?: string
  className?: string
}

/** Dymek — żółty kafel z ogonkiem w prawym dolnym rogu. */
function Bubble({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="relative inline-flex items-center"
      style={{ background: YELLOW, padding: '0.16em 0.2em' }}
    >
      <span style={{ color: BLACK, lineHeight: 1 }}>{children}</span>
      <span
        aria-hidden
        className="absolute"
        style={{
          right: '0.1em',
          top: '100%',
          width: '0.2em',
          height: '0.19em',
          background: YELLOW,
          clipPath: 'polygon(0 0, 100% 0, 100% 100%)',
        }}
      />
    </span>
  )
}

export default function Logo43OS({
  size = 28,
  variant = 'wordmark',
  onDark = false,
  tagline = 'System operacyjny dla instalatorów',
  className = '',
}: Logo43OSProps) {
  const ink = onDark ? '#FFFFFF' : BLACK

  if (variant === 'mark') {
    return (
      <span
        className={`inline-flex ${className}`}
        style={{ fontSize: size, fontWeight: 800, letterSpacing: '-0.04em' }}
        role="img"
        aria-label="43OS"
      >
        <Bubble>43</Bubble>
      </span>
    )
  }

  const wordmark = (
    <span
      className="inline-flex items-center"
      style={{
        fontSize: size,
        fontWeight: 800,
        letterSpacing: '-0.04em',
        gap: '0.2em',
        lineHeight: 1,
      }}
    >
      <span style={{ color: ink, lineHeight: 1 }}>43</span>
      <Bubble>OS</Bubble>
    </span>
  )

  if (variant === 'lockup') {
    return (
      <span
        className={`inline-flex flex-col items-start ${className}`}
        role="img"
        aria-label={`43OS — ${tagline}`}
      >
        {wordmark}
        <span
          style={{
            marginTop: size * 0.42,
            fontSize: Math.max(10, size * 0.31),
            fontWeight: 500,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: onDark ? 'rgba(255,255,255,0.72)' : '#888780',
            lineHeight: 1.35,
          }}
        >
          {tagline}
        </span>
      </span>
    )
  }

  return (
    <span className={`inline-flex ${className}`} role="img" aria-label="43OS">
      {wordmark}
    </span>
  )
}
